import { useState, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import { supabase } from '../lib/supabase';

const ASSIGNMENT_TYPES = [
  { value: 'others', label: 'Others' },
  { value: 'exam', label: 'Exam' },
  { value: 'quiz', label: 'Quiz' },
  { value: 'hw', label: 'Homework' },
  { value: 'cw', label: 'Classwork' }
];

const TYPE_ORDER = ['exam', 'quiz', 'hw', 'cw', 'others'];

const typeLabel = (value) => ASSIGNMENT_TYPES.find((t) => t.value === value)?.label || 'Others';

function groupAssignmentsByType(assignments) {
  const byType = {};
  TYPE_ORDER.forEach((t) => { byType[t] = []; });
  (assignments || []).forEach((a) => {
    const t = a.assignment_type || 'others';
    if (!byType[t]) byType[t] = [];
    byType[t].push(a);
  });
  return TYPE_ORDER.map((type) => ({ type, list: byType[type] || [] })).filter((g) => g.list.length > 0);
}

export default function AssignmentsTab({ levelCode, teacherId }) {
  const [assignments, setAssignments] = useState([]);
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [newAssignment, setNewAssignment] = useState({
    title: '',
    description: '',
    assignment_type: 'others',
    max_score: 10,
    due_date: '',
    teacher_id: teacherId
  });
  const [assignmentResponses, setAssignmentResponses] = useState({});
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);

  useEffect(() => {
    fetchAssignments();
  }, [levelCode]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('assignments')
        .select('*')
        .eq('level_code', levelCode)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAssignments(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  const fetchAssignmentResponses = async (assignmentId) => {
    try {
      const { data, error } = await supabase
        .from('assignment_submissions')
        .select(`
          *,
          student:student_id (
            id,
            name,
            code
          )
        `)
        .eq('assignment_id', assignmentId)
        .order('submitted_at', { ascending: false });

      if (error) throw error;
      setAssignmentResponses(prev => ({
        ...prev,
        [assignmentId]: data
      }));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAssignmentSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      if (editingAssignment) {
        const { error } = await supabase
          .from('assignments')
          .update({
            title: newAssignment.title,
            description: newAssignment.description,
            assignment_type: newAssignment.assignment_type || 'others',
            max_score: newAssignment.max_score,
            due_date: datetimeLocalToISO(newAssignment.due_date),
          })
          .eq('id', editingAssignment.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('assignments')
          .insert([{
            ...newAssignment,
            due_date: datetimeLocalToISO(newAssignment.due_date),
            level_code: levelCode,
            teacher_id: teacherId,
            created_at: new Date().toISOString()
          }]);

        if (error) throw error;
      }

      setNewAssignment({
        title: '',
        description: '',
        assignment_type: 'others',
        max_score: 10,
        due_date: '',
        teacher_id: teacherId
      });
      setShowAssignmentForm(false);
      setEditingAssignment(null);
      fetchAssignments();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGradeResponse = async (submissionId, score) => {
    try {
      // First get the submission to get the student_id and assignment_id
      const { data: submission, error: submissionError } = await supabase
        .from('assignment_submissions')
        .select('student_id, assignment_id')
        .eq('id', submissionId)
        .single();

      if (submissionError) throw submissionError;

      // Then upsert the grade
      const { error } = await supabase
        .from('grades')
        .upsert({
          assignment_id: submission.assignment_id,
          student_id: submission.student_id,
          score: score,
          graded_at: new Date().toISOString()
        }, { 
          onConflict: 'assignment_id,student_id'
        });

      if (error) throw error;
      
      // Refresh the responses to show updated grades
      if (selectedAssignment) {
        fetchAssignmentResponses(selectedAssignment.id);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteAssignment = async (id) => {
    if (window.confirm('Are you sure you want to delete this assignment?')) {
      try {
        const { error } = await supabase
          .from('assignments')
          .delete()
          .eq('id', id);

        if (error) throw error;
        fetchAssignments();
      } catch (err) {
        setError(err.message);
      }
    }
  };


  // Convert datetime-local string to ISO string (treating input as local time)
  const datetimeLocalToISO = (datetimeLocal) => {
    if (!datetimeLocal) return null;
    // datetime-local format: "2025-02-13T10:30"
    // Create date in local timezone, then convert to ISO
    const date = new Date(datetimeLocal);
    return date.toISOString();
  };

  // Format date for datetime-local input (from ISO string, showing local time)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    // Get local date components (not UTC)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const assignmentsByType = useMemo(() => groupAssignmentsByType(assignments), [assignments]);

  const startEditingAssignment = (assignment) => {
    setEditingAssignment(assignment);
    setNewAssignment({
      title: assignment.title,
      description: assignment.description,
      assignment_type: assignment.assignment_type || 'others',
      max_score: assignment.max_score,
      due_date: formatDateForInput(assignment.due_date),
      teacher_id: teacherId
    });
    setShowAssignmentForm(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 loading-spinner"></div>
          <p className="text-neutral-600">Loading assignments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-neutral-900">Assignments</h2>
          <p className="text-sm text-neutral-600">Create and manage assignments for your class</p>
        </div>
        
        <button
          onClick={() => {
            setEditingAssignment(null);
            setNewAssignment({
              title: '',
              description: '',
              assignment_type: 'others',
              max_score: 10,
              due_date: '',
              teacher_id: teacherId
            });
            setShowAssignmentForm(true);
          }}
          className="btn-primary px-3 py-2 text-sm font-medium whitespace-nowrap"
        >
          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          New Assignment
        </button>
      </div>

      {error && (
        <div className="card p-3 border-l-4 border-error-500 bg-error-50">
          <div className="flex items-center">
            <svg className="w-4 h-4 text-error-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-error-700 font-medium text-sm">{error}</p>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {assignments.length === 0 ? (
          <div className="card p-6 text-center">
            <div className="w-12 h-12 bg-neutral-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-neutral-900 mb-1">No assignments yet</h3>
            <p className="text-sm text-neutral-500">Create your first assignment to get started.</p>
          </div>
        ) : (
          assignmentsByType.map(({ type, list }) => (
            <div key={type}>
              <h3 className="text-sm font-semibold text-neutral-600 mb-2 px-1">{typeLabel(type)}</h3>
              <div className="space-y-3">
                {list.map((assignment) => (
                  <div key={assignment.id} className="card p-4">
                    <div className="flex flex-col gap-3 mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-bold text-neutral-900">
                          {assignment.title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <p className="text-xs text-neutral-500">
                            Due: {assignment.due_date ? new Date(assignment.due_date).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit'
                            }) : '—'}
                          </p>
                          <span className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded text-xs font-medium">
                            Max: {assignment.max_score}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedAssignment(assignment);
                            fetchAssignmentResponses(assignment.id);
                            setShowResponseModal(true);
                          }}
                          className="btn-primary text-xs px-2 py-1.5"
                        >
                          Responses
                        </button>
                        <button
                          onClick={() => startEditingAssignment(assignment)}
                          className="btn-secondary text-xs px-2 py-1.5"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteAssignment(assignment.id)}
                          className="btn-ghost text-error-600 hover:text-error-700 text-xs px-2 py-1.5"
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    <div className="prose prose-sm max-w-none">
                      <div className="p-2 -mx-2 rounded-lg text-sm">
                        <ReactMarkdown rehypePlugins={[rehypeSanitize]}>
                          {assignment.description}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Assignment Form Modal */}
      {showAssignmentForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center sm:justify-center p-0 sm:p-4 z-50 safe-top safe-bottom">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-large w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="sticky top-0 bg-white border-b border-neutral-200 p-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-neutral-900">
                {editingAssignment ? 'Edit Assignment' : 'New Assignment'}
              </h3>
              <button
                onClick={() => {
                  setShowAssignmentForm(false);
                  setEditingAssignment(null);
                  setNewAssignment({
                    title: '',
                    description: '',
                    assignment_type: 'others',
                    max_score: 10,
                    due_date: '',
                    teacher_id: teacherId
                  });
                }}
                className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4 sm:p-6">
              {error && (
                <div className="mb-4 p-3 bg-error-50 border border-error-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <svg className="h-4 w-4 text-error-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-error-700 font-medium text-sm">{error}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleAssignmentSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Title</label>
                  <input
                    type="text"
                    value={newAssignment.title}
                    onChange={(e) => setNewAssignment({ ...newAssignment, title: e.target.value })}
                    className="input text-sm"
                    placeholder="Enter assignment title"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Type</label>
                  <select
                    value={newAssignment.assignment_type || 'others'}
                    onChange={(e) => setNewAssignment({ ...newAssignment, assignment_type: e.target.value })}
                    className="input text-sm"
                  >
                    {ASSIGNMENT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Description</label>
                  <textarea
                    value={newAssignment.description}
                    onChange={(e) => setNewAssignment({ ...newAssignment, description: e.target.value })}
                    className="input text-sm"
                    rows={6}
                    placeholder="Enter assignment description"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Maximum Score</label>
                    <input
                      type="number"
                      value={newAssignment.max_score}
                      onChange={(e) => setNewAssignment({ ...newAssignment, max_score: parseFloat(e.target.value) || 0 })}
                      className="input text-sm"
                      min="0"
                      step="0.5"
                      required
                    />
                    <p className="text-xs text-neutral-500 mt-1">Use 0 for extra credit only.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Due Date</label>
                    <input
                      type="datetime-local"
                      value={newAssignment.due_date}
                      onChange={(e) => setNewAssignment({ ...newAssignment, due_date: e.target.value })}
                      className="input text-sm"
                      required
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAssignmentForm(false);
                      setEditingAssignment(null);
                      setNewAssignment({
                        title: '',
                        description: '',
                        assignment_type: 'others',
                        max_score: 10,
                        due_date: '',
                        teacher_id: teacherId
                      });
                    }}
                    className="btn-secondary text-sm py-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary text-sm py-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 loading-spinner mr-2"></div>
                        {editingAssignment ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      editingAssignment ? 'Update' : 'Create Assignment'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Response Modal */}
      {showResponseModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center sm:justify-center p-0 sm:p-4 z-50 safe-top safe-bottom">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-large w-full sm:max-w-4xl max-h-[90vh] overflow-hidden animate-scale-in">
            <div className="sticky top-0 bg-white border-b border-neutral-200 p-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-neutral-900 truncate pr-2">
                Submissions: {selectedAssignment?.title}
              </h3>
              <button
                onClick={() => {
                  setShowResponseModal(false);
                  setSelectedAssignment(null);
                }}
                className="p-2 hover:bg-neutral-100 rounded-lg transition-colors flex-shrink-0"
              >
                <svg className="w-5 h-5 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4 sm:p-6 max-h-[60vh] overflow-y-auto">
              <div className="space-y-4">
                {assignmentResponses[selectedAssignment?.id]?.map((submission) => (
                  <div key={submission.id} className="card p-4">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
                          <span className="text-white font-bold text-sm">
                            {submission.student.name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <h4 className="font-medium text-neutral-900">{submission.student.name}</h4>
                          {submission.student.code && <p className="text-sm text-neutral-500">{submission.student.code}</p>}
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        {submission.score !== null ? (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-neutral-700">
                              Score: {submission.score}{selectedAssignment.max_score === 0 ? ' (EC)' : `/${selectedAssignment.max_score}`}
                            </span>
                            <button
                              onClick={() => {
                                const maxStr = selectedAssignment.max_score === 0 ? 'no max (extra credit)' : selectedAssignment.max_score;
                                const newScore = prompt(`Enter new score (max ${maxStr}):`, submission.score);
                                if (newScore !== null) {
                                  const score = parseFloat(newScore);
                                  const valid = !isNaN(score) && score >= 0 && (selectedAssignment.max_score === 0 || score <= selectedAssignment.max_score);
                                  if (valid) {
                                    handleGradeResponse(submission.id, score);
                                  } else {
                                    alert(selectedAssignment.max_score === 0
                                      ? 'Please enter a valid score (0 or higher).'
                                      : 'Please enter a valid score between 0 and ' + selectedAssignment.max_score);
                                  }
                                }
                              }}
                              className="btn-primary text-sm px-3 py-2"
                            >
                              Update Grade
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              min="0"
                              max={selectedAssignment.max_score === 0 ? undefined : selectedAssignment.max_score}
                              step="any"
                              inputMode="decimal"
                              className="w-20 input text-center"
                              placeholder="Score"
                              onChange={(e) => handleGradeResponse(submission.id, parseFloat(e.target.value))}
                            />
                            <span className="text-sm text-neutral-500">
                              /{selectedAssignment.max_score === 0 ? 'EC' : selectedAssignment.max_score}
                            </span>
                            <button
                              onClick={() => {
                                const score = parseFloat(document.querySelector(`input[placeholder="Score"]`).value);
                                const valid = !isNaN(score) && score >= 0 && (selectedAssignment.max_score === 0 || score <= selectedAssignment.max_score);
                                if (valid) {
                                  handleGradeResponse(submission.id, score);
                                } else {
                                  alert(selectedAssignment.max_score === 0
                                    ? 'Please enter a valid score (0 or higher).'
                                    : 'Please enter a valid score between 0 and ' + selectedAssignment.max_score);
                                }
                              }}
                              className="btn-primary text-sm px-3 py-2"
                            >
                              Grade
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="prose prose-sm max-w-none">
                      <ReactMarkdown rehypePlugins={[rehypeSanitize]}>
                        {submission.content}
                      </ReactMarkdown>
                    </div>
                    {submission.submitted_at && (
                      <p className="text-sm text-neutral-500 mt-2">
                        Submitted: {new Date(submission.submitted_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
